<template>
  <div class="container">
    <h3 v-if="title">{{ title }}</h3>
    <card title="Subscription" v-loading="loading.subscription">
      <span slot="header">
        &nbsp;
        <span v-if="payload.is_vip" style="color: #67C23A; font-style: italic; font-weight: 700">
          VIP
        </span>
        <span v-if="payload.is_manually_created" style="color: #E6A23C; font-style: italic">
          Created via admin panel
        </span>
        <span v-if="payload.is_deleted" style="color: #F56C6C; font-style: italic; font-weight: 500; opacity: 0.5">
          (deleted)
        </span>
      </span>
      <el-form label-width="250px" label-position="left" size="small">
        <el-form-item label="Created at">
          <div class="block">
            <el-date-picker v-model="payload.created_at" type="datetime">
            </el-date-picker>
          </div>
        </el-form-item>
        <el-form-item label="Cancelled requested date">
          <div class="block">
            <el-date-picker
              v-model="payload.cancelled_requested_at_date"
              type="datetime"
            >
            </el-date-picker>
          </div>
        </el-form-item>
        <el-form-item label="Subscription ID">
          <el-input v-model="payload.subscription_id" />
        </el-form-item>
        <el-form-item label="Plan ID">
          <el-input v-model="payload.plan_id" />
        </el-form-item>
        <el-form-item label="Product name">
          <el-input v-model="payload.product_name" />
        </el-form-item>
        <el-form-item label="Interval">
          <el-select v-model="payload.interval">
            <el-option value="monthly" label="Monthly" />
            <el-option value="annually" label="Annually" />
          </el-select>
        </el-form-item>
        <el-form-item label="Master">
          <el-switch v-model="payload.is_master"></el-switch>
        </el-form-item>
        <el-form-item label="Paused">
          <el-switch v-model="payload.is_paused"></el-switch>
        </el-form-item>
        <el-form-item label="Archived">
          <el-switch v-model="payload.is_archived"></el-switch>
        </el-form-item>
        <el-form-item label="Test">
          <el-switch v-model="payload.is_test"></el-switch>
        </el-form-item>
        <el-form-item label="Trial">
          <el-switch v-model="payload.is_trial"></el-switch>
        </el-form-item>
        <el-form-item label="Should cancel at period end">
          <el-switch v-model="payload.should_cancel_at_period_end"></el-switch>
        </el-form-item>
        <el-form-item label="Trial end">
          <div class="block">
            <el-date-picker v-model="payload.trial_end" type="datetime">
            </el-date-picker>
          </div>
        </el-form-item>
        <el-form-item label="Is paused and unpaid">
          <el-switch v-model="payload.is_paused_and_unpaid"></el-switch>
        </el-form-item>
        <el-form-item label="Quantity">
          <el-input-number
            v-model="payload.quantity"
            :min="1"
            :step="1"
            step-strictly
          />
        </el-form-item>
        <el-form-item label="Price per unit">
          <el-input-number
            v-model="payload.price_per_unit"
            :min="1"
            :step="1"
            :precision="2"
          />
        </el-form-item>
        <el-form-item label="Status">
          <el-select v-model="payload.status">
            <el-option value="active" label="active" />
            <el-option value="canceled" label="canceled" />
          </el-select>
        </el-form-item>
        <el-form-item label="Updated at">
          <div class="block">
            <el-date-picker v-model="payload.updated_at" type="datetime">
            </el-date-picker>
          </div>
        </el-form-item>
        <el-form-item label="Actions">
          <el-button @click="save" type="primary">Save</el-button>
          <el-button @click="goToTeam">View team</el-button>
          <div class="confirm-buttons">
            <Confirm @confirm="restore" v-if="payload.status === 'canceled'">
              <el-button type="success">Restore</el-button>
            </Confirm>
            <Confirm @confirm="downgrade" v-else>
              <el-button type="danger">Downgrade</el-button>
            </Confirm>
          </div>
          <Confirm @confirm="onDeleteSubscription">
            <el-button type="danger">Delete</el-button>
          </Confirm>
        </el-form-item>
      </el-form>
    </card>
  </div>
</template>

<script>
import { cloneDeep, pick, upperFirst } from "lodash";
import { mapActions, mapGetters } from "vuex";
import Card from "@/components/Card";
import Confirm from "@/components/Confirm";

export default {
  data() {
    return {
      payload: {},
    };
  },
  props: {
    subscriptionType: {
      type: String,
      validator: (val) => ["paypal", "stripe"].includes(val),
    },
  },
  components: {
    Card,
    Confirm,
  },
  created() {
    this.fetchData();
  },
  computed: {
    ...mapGetters(["loading", "subscription"]),
    title() {
      const { subscription_id } = this.subscription;
      return upperFirst(
        `${this.subscriptionType} subscription: ${subscription_id}`
      );
    },
  },
  methods: {
    ...mapActions([
      "getSubscription",
      "updateSubscription",
      "deleteSubscription",
    ]),
    async fetchData() {
      try {
        const { subscriptionType } = this;
        const { query: id } = this.$route.params;
        const subscription = await this.getSubscription({
          subscriptionType,
          id,
        });
        this.payload = cloneDeep(subscription);
        this.payload.price_per_unit = subscription.price_per_unit / 100;
      } catch (e) {
        console.error(e);
      }
    },
    goToTeam() {
      const { team } = this.payload;
      this.$router.push(`/customers/team/${team}`);
    },
    async save() {
      try {
        const { subscriptionType } = this;
        const { id } = this.payload;
        // const payload = cloneDeep(rest);
        const payload = pick(this.payload, [
          "created_at",
          "cancelled_requested_at_date",
          "subscription_id",
          "plan_id",
          "product_name",
          "interval",
          "is_master",
          "is_paused",
          "is_archived",
          "is_test",
          "is_trial",
          "should_cancel_at_period_end",
          "trial_end",
          "is_paused_and_unpaid",
          "quantity",
          "price_per_unit",
          "status",
          "updated_at",
        ]);
        payload.price_per_unit = payload.price_per_unit * 100;

        await this.updateSubscription({ subscriptionType, id, payload });
        this.$message.success("Saved successfully");
      } catch (error) {
        this.$notify.error({
          title: "Save failed. :(",
          message: error.message,
        });
      }
    },
    downgrade() {
      this.changeStatus({ status: "canceled" });
    },
    restore() {
      this.changeStatus({ status: "active" });
    },
    async changeStatus(postData) {
      try {
        const { subscriptionType } = this;
        const { id } = this.payload;

        await this.$api.post(`/cs/subscriptions/${id}/status`, postData);
        // this.$trackEvent('admin.customer-success.cancel-subscription', { id, ...postData });

        this.$message.success("Updated successfully");
        const subscription = await this.getSubscription({
          subscriptionType,
          id,
        });
        this.payload = cloneDeep(subscription);
      } catch (error) {
        this.$notify.error({
          title: "Update failed. :(",
          message: error.message,
        });
      }
    },
    async onDeleteSubscription() {
      try {
        const { id } = this.payload;
        await this.deleteSubscription({ id, payload: { is_deleted: true } });
        this.$message.success("Subscription Delete Successfully!");
      } catch (e) {
        this.$notify.error({
          title: "Failed",
          message: e.message,
        });
      }
    },
  },
  watch: {
    "$route.params"() {
      this.fetchData();
    },
  },
};
</script>

<style scoped>
.container > * + * {
  margin-top: 10px;
}

.confirm-buttons {
  display: inline-block;
  margin: 0 10px;
}
</style>
